import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import SignatureDialog from '@/components/SignatureDialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { FileDown, PenLine, Plus, Sparkles, Eye, Trash2, ChevronsUpDown, AlertTriangle, KeyRound, CheckCircle2 } from 'lucide-react';
import FreeTextNotice from '@/components/FreeTextNotice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getParisToday = () => {
  const parts = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(new Date()).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  return `${parts.year}-${parts.month}-${parts.day}`;
};

// Palette de couleurs bien distinctes, attribuées de façon stable à chaque
// site (toujours la même couleur pour un même site), pour qu'un admin
// parcourant plusieurs sites les distingue au premier coup d'oeil.
const SITE_COLOR_PALETTE = [
  'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800',
  'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800',
  'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800',
  'bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800',
  'bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800',
  'bg-teal-100 text-teal-700 border-teal-300 dark:bg-teal-950/40 dark:text-teal-300 dark:border-teal-800',
  'bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-800',
  'bg-indigo-100 text-indigo-700 border-indigo-300 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800',
];
const getSiteColorClass = (siteId) => {
  if (!siteId) return SITE_COLOR_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < siteId.length; i++) hash = (hash * 31 + siteId.charCodeAt(i)) % SITE_COLOR_PALETTE.length;
  return SITE_COLOR_PALETTE[Math.abs(hash) % SITE_COLOR_PALETTE.length];
};

// Diff mot-à-mot entre deux textes (ex: contenu avant/après une modification
// de compte rendu), pour un affichage "suivi des modifications" : le texte
// supprimé est barré en rouge, le texte ajouté est en vert. Implémenté "à la
// main" (recherche du plus long sous-ensemble commun) pour éviter d'ajouter
// une dépendance externe au projet.
const diffWords = (oldText, newText) => {
  const oldTokens = (oldText || '').split(/(\s+)/);
  const newTokens = (newText || '').split(/(\s+)/);
  const n = oldTokens.length, m = newTokens.length;

  // Garde-fou : pour un texte très long, la DP O(n*m) devient coûteuse.
  // Dans ce cas rare, on retombe sur un diff grossier (tout remplacé).
  if (n * m > 400000) {
    return [
      { type: 'removed', value: oldText || '' },
      { type: 'added', value: newText || '' }
    ].filter(t => t.value);
  }

  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = oldTokens[i] === newTokens[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const raw = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (oldTokens[i] === newTokens[j]) {
      raw.push({ type: 'equal', value: oldTokens[i] });
      i++; j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      raw.push({ type: 'removed', value: oldTokens[i] });
      i++;
    } else {
      raw.push({ type: 'added', value: newTokens[j] });
      j++;
    }
  }
  while (i < n) { raw.push({ type: 'removed', value: oldTokens[i] }); i++; }
  while (j < m) { raw.push({ type: 'added', value: newTokens[j] }); j++; }

  // Fusionne les tokens consécutifs de même type pour un rendu plus propre
  const merged = [];
  for (const tok of raw) {
    const last = merged[merged.length - 1];
    if (last && last.type === tok.type) last.value += tok.value;
    else merged.push({ ...tok });
  }
  return merged;
};

// Affiche un texte avec le suivi des modifications : ajouts en vert, suppressions barrées en rouge.
const DiffText = ({ oldText, newText, className = '' }) => {
  const tokens = React.useMemo(() => diffWords(oldText, newText), [oldText, newText]);
  return (
    <p className={`whitespace-pre-wrap ${className}`}>
      {tokens.map((tok, idx) => {
        if (tok.type === 'added') {
          return <span key={idx} className="text-green-600 dark:text-green-400 font-medium">{tok.value}</span>;
        }
        if (tok.type === 'removed') {
          return <span key={idx} className="text-red-600 dark:text-red-400 line-through">{tok.value}</span>;
        }
        return <span key={idx}>{tok.value}</span>;
      })}
    </p>
  );
};

const emptyForm = (siteId) => ({
  site_id: siteId || '',
  report_date: getParisToday(),
  time_start: '',
  time_end: '',
  subject: '',
  content: '',
  signature: null
});

const AgentReportForm = ({ user, onLogout }) => {
  const [sites, setSites] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [signing, setSigning] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [previewReport, setPreviewReport] = useState(null);
  const [editingReport, setEditingReport] = useState(null);
  const [editForm, setEditForm] = useState({ subject: '', content: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [filterSubject, setFilterSubject] = useState('');
  const [filterSite, setFilterSite] = useState('all');
  const [filterAuthor, setFilterAuthor] = useState('all');

  const isSiteAccount = user?.role === 'site';
  const canWrite = user?.permission_overrides?.write_agent_reports ?? ['admin', 'chef_poste', 'agent', 'site'].includes(user?.role);
  // Un compte de site partagé peut rédiger un compte rendu (au nom d'un
  // agent identifié par code personnel) mais ne peut jamais les consulter -
  // voir get_agent_report_reader côté serveur.
  const [siteAgents, setSiteAgents] = useState([]);
  const [redacteurId, setRedacteurId] = useState('');
  const [redacteurPickerOpen, setRedacteurPickerOpen] = useState(false);
  const [redacteurPin, setRedacteurPin] = useState('');
  const [redacteurPinDialogOpen, setRedacteurPinDialogOpen] = useState(false);
  const [pinChecking, setPinChecking] = useState(false);
  const [pinVerified, setPinVerified] = useState(false);
  const [pinError, setPinError] = useState('');
  // L'auteur peut toujours modifier son propre compte rendu ; la permission
  // "edit_agent_reports" (admin par défaut, accordable via l'administration)
  // permet en plus de modifier ceux rédigés par d'autres.
  const canEditOthersReports = user?.permission_overrides?.edit_agent_reports ?? (user?.role === 'admin');
  const canEditReport = (r) => canWrite && (r.author_id === user?.id || canEditOthersReports);
  // Permission "delete_agent_reports" (admin par défaut, accordable via l'administration)
  const canDeleteReports = user?.permission_overrides?.delete_agent_reports ?? (user?.role === 'admin');

  useEffect(() => {
    loadSites();
    if (!isSiteAccount) loadReports();
    else setLoading(false);
    // eslint-disable-next-line
  }, []);

  // Agents du site sélectionné, pour désigner le rédacteur (compte de site uniquement).
  useEffect(() => {
    if (!isSiteAccount || !formData.site_id) { setSiteAgents([]); return; }
    const token = localStorage.getItem('token');
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/sites/${formData.site_id}/agents`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setSiteAgents(await res.json());
      } catch { /* silencieux */ }
    })();
    setRedacteurId('');
    setRedacteurPin('');
    setPinVerified(false);
    setPinError('');
    // eslint-disable-next-line
  }, [isSiteAccount, formData.site_id]);

  const verifyRedacteurPin = async (pin, agentId) => {
    setPinChecking(true);
    setPinError('');
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/agent-reports/verify-redacteur-pin`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ redacteur_id: agentId, pin })
      });
      if (res.ok) {
        setPinVerified(true);
        setRedacteurPinDialogOpen(false);
      } else {
        const err = await res.json();
        setPinError(err.detail || 'Code incorrect');
        setPinVerified(false);
        setRedacteurPin('');
      }
    } catch {
      setPinError('Erreur de connexion, merci de réessayer');
      setPinVerified(false);
    } finally {
      setPinChecking(false);
    }
  };

  const loadSites = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/sites`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setSites(data);
        if (data.length > 0) setFormData(prev => ({ ...prev, site_id: prev.site_id || data[0].id }));
        // Un non-admin n'a pas la possibilité de choisir "Tous les sites" :
        // son site (unique, ou celui choisi à la connexion) est sélectionné directement.
        if (user?.role !== 'admin' && data.length > 0) {
          setFilterSite(prev => (prev === 'all' ? data[0].id : prev));
        }
      }
    } catch { /* silencieux */ }
  };

  const loadReports = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/agent-reports`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setReports(await res.json());
    } catch { /* silencieux */ }
    finally { setLoading(false); }
  };

  const openNewForm = () => {
    setFormData(emptyForm(sites[0]?.id));
    setRedacteurId('');
    setRedacteurPin('');
    setPinVerified(false);
    setPinError('');
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!formData.site_id || !formData.subject.trim() || !formData.content.trim()) {
      toast.error("L'objet et le contenu du compte rendu sont obligatoires");
      return;
    }
    if (!formData.signature) {
      toast.error("La signature est obligatoire avant l'enregistrement");
      return;
    }
    if (isSiteAccount && (!redacteurId || !pinVerified)) {
      toast.warning('Sélectionnez l\'agent rédacteur et confirmez son code personnel');
      return;
    }
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/agent-reports`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          ...(isSiteAccount ? { redacteur_id: redacteurId, redacteur_pin: redacteurPin } : {})
        })
      });
      if (res.ok) {
        toast.success('Compte rendu enregistré');
        setShowForm(false);
        if (!isSiteAccount) loadReports();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async (reportId) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/agent-reports/${reportId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `compte_rendu_${reportId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        toast.error('Erreur lors du téléchargement');
      }
    } catch {
      toast.error('Erreur de connexion');
    }
  };

  const handleCorrectText = async () => {
    if (!formData.content.trim()) {
      toast.error('Rédigez du texte avant de le corriger');
      return;
    }
    setCorrecting(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/agent-reports/correct-text`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: formData.content })
      });
      if (res.ok) {
        const data = await res.json();
        setFormData(prev => ({ ...prev, content: data.corrected_text }));
        toast.success('Texte corrigé');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de la correction');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setCorrecting(false);
    }
  };

  const getSiteName = (siteId) => sites.find(s => s.id === siteId)?.name || 'Site inconnu';

  // Liste des rédacteurs distincts présents dans les comptes rendus chargés,
  // pour alimenter le filtre "Rédacteur".
  const authors = useMemo(() => {
    const map = new Map();
    reports.forEach(r => { if (!map.has(r.author_id)) map.set(r.author_id, r.author_name); });
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  }, [reports]);

  const filteredReports = useMemo(() => {
    const q = filterSubject.trim().toLowerCase();
    return reports.filter(r => {
      if (q && !r.subject.toLowerCase().includes(q)) return false;
      if (filterSite !== 'all' && r.site_id !== filterSite) return false;
      if (filterAuthor !== 'all' && r.author_id !== filterAuthor) return false;
      return true;
    });
  }, [reports, filterSubject, filterSite, filterAuthor]);

  const hasActiveFilters = filterSubject.trim() !== '' || filterSite !== 'all' || filterAuthor !== 'all';
  const resetFilters = () => { setFilterSubject(''); setFilterSite('all'); setFilterAuthor('all'); };

  const openPreview = (r) => setPreviewReport(r);

  const openEdit = (r) => {
    setEditingReport(r);
    setEditForm({ subject: r.subject, content: r.content });
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Supprimer définitivement ce compte rendu ? Cette action est irréversible.')) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/agent-reports/${reportId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Compte rendu supprimé');
        setPreviewReport(null);
        loadReports();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de la suppression');
      }
    } catch {
      toast.error('Erreur de connexion');
    }
  };

  const handleSaveEdit = async () => {
    if (!editForm.subject.trim() || !editForm.content.trim()) {
      toast.error("L'objet et le contenu du compte rendu sont obligatoires");
      return;
    }
    setSavingEdit(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/agent-reports/${editingReport.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });
      if (res.ok) {
        toast.success('Compte rendu modifié');
        setEditingReport(null);
        loadReports();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de la modification');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 animate-fade-in" data-testid="agent-report-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
              Comptes rendus
            </h1>
            <p className="text-gray-600 dark:text-slate-300">Rédigez et consultez vos comptes rendus d'intervention</p>
          </div>
          {canWrite && !showForm && (
            <Button className="btn-primary" onClick={openNewForm} data-testid="new-agent-report-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nouveau compte rendu
            </Button>
          )}
        </div>

        {showForm && (
          <Card className="glass-effect">
            <CardHeader><CardTitle className="flex items-center gap-2"><PenLine className="h-5 w-5" /> Rédiger un compte rendu</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <Label>Site</Label>
                  <Select value={formData.site_id} onValueChange={(v) => setFormData({ ...formData, site_id: v })}>
                    <SelectTrigger data-testid="agent-report-site"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={formData.report_date} onChange={(e) => setFormData({ ...formData, report_date: e.target.value })} />
                </div>
                <div>
                  <Label>Heure de début</Label>
                  <Input type="time" value={formData.time_start} onChange={(e) => setFormData({ ...formData, time_start: e.target.value })} />
                </div>
                <div>
                  <Label>Heure de fin</Label>
                  <Input type="time" value={formData.time_end} onChange={(e) => setFormData({ ...formData, time_end: e.target.value })} />
                </div>
              </div>

              {isSiteAccount && (
                <div className="space-y-2">
                  <Label>Agent rédacteur *</Label>
                  <Popover open={redacteurPickerOpen} onOpenChange={setRedacteurPickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        disabled={!formData.site_id}
                        className="w-full justify-between font-normal"
                        data-testid="agent-report-redacteur-picker-trigger"
                      >
                        {siteAgents.find((a) => a.id === redacteurId)?.name || (formData.site_id ? 'Sélectionnez un agent' : 'Choisissez d\'abord un site')}
                        <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0">
                      <Command>
                        <CommandInput placeholder="Rechercher un agent..." />
                        <CommandList>
                          <CommandEmpty>Aucun agent trouvé pour ce site.</CommandEmpty>
                          <CommandGroup>
                            {siteAgents.map((a) => (
                              <CommandItem
                                key={a.id}
                                value={a.name}
                                onSelect={() => {
                                  setRedacteurId(a.id);
                                  setRedacteurPin('');
                                  setPinVerified(false);
                                  setPinError('');
                                  setRedacteurPickerOpen(false);
                                  setRedacteurPinDialogOpen(true);
                                }}
                              >
                                {a.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Le compte rendu sera rédigé au nom de cet agent.</p>

                  {redacteurId && (
                    <div className="pt-1">
                      {pinVerified ? (
                        <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                          <CheckCircle2 className="h-4 w-4 shrink-0" />
                          <span>Code personnel confirmé</span>
                          <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="h-auto p-0 text-xs"
                            onClick={() => { setRedacteurPin(''); setPinVerified(false); setPinError(''); setRedacteurPinDialogOpen(true); }}
                          >
                            Modifier
                          </Button>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-2"
                          onClick={() => setRedacteurPinDialogOpen(true)}
                          data-testid="agent-report-redacteur-pin-open-btn"
                        >
                          <KeyRound className="h-4 w-4" />
                          Saisir le code personnel de l'agent *
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div>
                <Label>Objet du compte rendu</Label>
                <Input
                  placeholder="Ex: Compte rendu d'intervention, incident, événement particulier..."
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  data-testid="agent-report-subject"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Contenu</Label>
                  <Button
                    variant="outline" size="sm"
                    onClick={handleCorrectText}
                    disabled={correcting}
                    data-testid="correct-text-btn"
                  >
                    <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                    {correcting ? 'Correction en cours...' : 'Corriger mes fautes'}
                  </Button>
                </div>
                <Textarea
                  placeholder="Décrivez en détail le déroulement de votre intervention, les faits constatés, les actions menées..."
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={10}
                  data-testid="agent-report-content"
                />
                <FreeTextNotice withSpellcheck className="mt-1" />
              </div>
              <div>
                <Label>Signature *</Label>
                {formData.signature ? (
                  <div className="flex items-center gap-3">
                    <img src={formData.signature} alt="signature" className="h-16 border rounded bg-white" />
                    <Button variant="outline" size="sm" onClick={() => setSigning(true)}>Refaire</Button>
                  </div>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => setSigning(true)} data-testid="sign-agent-report-btn">
                    Signer
                  </Button>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowForm(false)}>Annuler</Button>
                <Button className="flex-1 btn-primary" onClick={handleSubmit} disabled={saving} data-testid="submit-agent-report-btn">
                  {saving ? 'Enregistrement...' : 'Enregistrer et générer le PDF'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {isSiteAccount && (
          <p className="text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3">
            Ce compte partagé peut rédiger des comptes rendus mais ne peut pas les consulter : la lecture se fait
            depuis le compte personnel de l'agent rédacteur, ou celui d'un chef de poste/admin du site.
          </p>
        )}

        {!isSiteAccount && !loading && reports.length > 0 && (
          <Card className="glass-effect">
            <CardContent className="py-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                <div className="md:col-span-2">
                  <Label>Titre</Label>
                  <Input
                    placeholder="Rechercher par objet..."
                    value={filterSubject}
                    onChange={(e) => setFilterSubject(e.target.value)}
                    data-testid="agent-report-filter-subject"
                  />
                </div>
                <div>
                  <Label>Site</Label>
                  <Select value={filterSite} onValueChange={setFilterSite}>
                    <SelectTrigger data-testid="agent-report-filter-site"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {user?.role === 'admin' && <SelectItem value="all">Tous les sites</SelectItem>}
                      {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Rédacteur</Label>
                  <Select value={filterAuthor} onValueChange={setFilterAuthor}>
                    <SelectTrigger data-testid="agent-report-filter-author"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les rédacteurs</SelectItem>
                      {authors.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {hasActiveFilters && (
                <div className="mt-3 flex items-center justify-between">
                  <p className="text-xs text-slate-400">
                    {filteredReports.length} résultat{filteredReports.length !== 1 ? 's' : ''}
                  </p>
                  <Button variant="ghost" size="sm" onClick={resetFilters}>Réinitialiser les filtres</Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {!isSiteAccount && (loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : reports.length === 0 ? (
          <Card className="glass-effect">
            <CardContent className="py-12 text-center">
              <PenLine className="h-12 w-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-slate-300">Aucun compte rendu pour le moment</p>
            </CardContent>
          </Card>
        ) : filteredReports.length === 0 ? (
          <Card className="glass-effect">
            <CardContent className="py-12 text-center">
              <PenLine className="h-12 w-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-slate-300">Aucun compte rendu ne correspond à ces filtres</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredReports.map(r => (
              <Card key={r.id} className="glass-effect" data-testid={`agent-report-${r.id}`}>
                <CardContent className="py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge variant="outline" className={getSiteColorClass(r.site_id)}>{getSiteName(r.site_id)}</Badge>
                      <span className="text-xs text-slate-400">
                        {new Date(r.report_date + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                        {r.time_start && ` — ${r.time_start} à ${r.time_end || '?'}`}
                      </span>
                    </div>
                    <p className="font-medium text-slate-800 dark:text-slate-100">{r.subject}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Par {r.author_name}
                      {r.edit_history?.length > 0 && (
                        <span className="ml-1 italic">
                          · modifié{r.edit_history.length > 1 ? ` (${r.edit_history.length}x)` : ''}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => openPreview(r)} data-testid={`preview-agent-report-${r.id}`}>
                      <Eye className="h-4 w-4 mr-2" />
                      Aperçu
                    </Button>
                    {canEditReport(r) && (
                      <Button variant="outline" size="sm" onClick={() => openEdit(r)} data-testid={`edit-agent-report-${r.id}`}>
                        <PenLine className="h-4 w-4 mr-2" />
                        Modifier
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => handleDownloadPdf(r.id)} data-testid={`download-agent-report-${r.id}`}>
                      <FileDown className="h-4 w-4 mr-2" />
                      PDF
                    </Button>
                    {canDeleteReports && (
                      <Button
                        variant="outline" size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                        onClick={() => handleDeleteReport(r.id)}
                        data-testid={`delete-agent-report-${r.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ))}
      </div>

      {/* Popup de saisie du code personnel du rédacteur : dès les 4 chiffres
          renseignés, le code est vérifié immédiatement auprès du serveur -
          elle ne se ferme que si le code est le bon. */}
      <Dialog open={redacteurPinDialogOpen} onOpenChange={(open) => { if (!pinChecking) setRedacteurPinDialogOpen(open); }}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <KeyRound className="h-4 w-4 text-indigo-600" />
              Code personnel
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 dark:text-slate-400">
            Demandé à {siteAgents.find((a) => a.id === redacteurId)?.name || 'l\'agent sélectionné'} pour confirmer son identité.
          </p>
          <div className="flex flex-col items-center gap-2 py-2">
            <InputOTP
              maxLength={4}
              inputMode="numeric"
              value={redacteurPin}
              onChange={(value) => { setRedacteurPin(value); setPinError(''); }}
              onComplete={(value) => verifyRedacteurPin(value, redacteurId)}
              disabled={pinChecking}
              data-testid="agent-report-redacteur-pin-otp"
              autoFocus
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} mask />
                <InputOTPSlot index={1} mask />
                <InputOTPSlot index={2} mask />
                <InputOTPSlot index={3} mask />
              </InputOTPGroup>
            </InputOTP>

            {pinChecking && (
              <p className="text-xs text-slate-500 dark:text-slate-400">Vérification...</p>
            )}

            {pinError && (
              <div
                className="flex items-start gap-2 p-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 text-xs w-full"
                data-testid="agent-report-redacteur-pin-error"
              >
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>{pinError}</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <SignatureDialog
        open={signing}
        onClose={() => setSigning(false)}
        onSign={(sig) => { setFormData(prev => ({ ...prev, signature: sig })); setSigning(false); }}
        title="Signature de l'agent"
      />

      <Dialog open={!!previewReport} onOpenChange={(o) => !o && setPreviewReport(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{previewReport?.subject}</DialogTitle>
          </DialogHeader>
          {previewReport && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                <Badge variant="outline" className={getSiteColorClass(previewReport.site_id)}>{getSiteName(previewReport.site_id)}</Badge>
                <span>
                  {new Date(previewReport.report_date + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
                {previewReport.time_start && <span>{previewReport.time_start} à {previewReport.time_end || '?'}</span>}
                <span>Par {previewReport.author_name}</span>
              </div>

              <div>
                <Label className="text-xs uppercase text-slate-400">Contenu</Label>
                <p className="whitespace-pre-wrap text-sm mt-1 text-slate-700 dark:text-slate-200">{previewReport.content}</p>
              </div>

              {previewReport.signature && (
                <div>
                  <Label className="text-xs uppercase text-slate-400">Signature</Label>
                  <img src={previewReport.signature} alt="signature" className="h-16 border rounded bg-white mt-1" />
                </div>
              )}

              {previewReport.edit_history?.length > 0 && (
                <div>
                  <Label className="text-xs uppercase text-slate-400">Historique des modifications</Label>
                  <div className="space-y-2 mt-1">
                    {previewReport.edit_history.map((h, i) => (
                      <div key={i} className="text-xs border rounded p-2 bg-slate-50 dark:bg-slate-800/50">
                        <p className="font-medium text-slate-600 dark:text-slate-300">
                          Modifié par {h.edited_by_name} le {new Date(h.edited_at).toLocaleString('fr-FR')}
                        </p>
                        <p className="mt-1 text-[10px] text-slate-400">
                          <span className="text-green-600 dark:text-green-400">■</span> ajouté &nbsp;
                          <span className="text-red-600 dark:text-red-400">■</span> supprimé
                        </p>
                        {h.old_subject !== h.new_subject && (
                          <div className="mt-1">
                            <span className="text-slate-400">Objet : </span>
                            <DiffText oldText={h.old_subject} newText={h.new_subject} className="inline" />
                          </div>
                        )}
                        {h.old_content !== h.new_content && (
                          <div className="mt-1">
                            <span className="text-slate-400 block mb-0.5">Contenu :</span>
                            <DiffText oldText={h.old_content} newText={h.new_content} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                {canEditReport(previewReport) && (
                  <Button variant="outline" onClick={() => { openEdit(previewReport); setPreviewReport(null); }}>
                    <PenLine className="h-4 w-4 mr-2" />
                    Modifier
                  </Button>
                )}
                <Button variant="outline" onClick={() => handleDownloadPdf(previewReport.id)}>
                  <FileDown className="h-4 w-4 mr-2" />
                  PDF
                </Button>
                {canDeleteReports && (
                  <Button
                    variant="outline"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                    onClick={() => handleDeleteReport(previewReport.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Supprimer
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingReport} onOpenChange={(o) => !o && setEditingReport(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Modifier le compte rendu</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Objet</Label>
              <Input
                value={editForm.subject}
                onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
                data-testid="edit-agent-report-subject"
              />
            </div>
            <div>
              <Label>Contenu</Label>
              <Textarea
                rows={10}
                value={editForm.content}
                onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                data-testid="edit-agent-report-content"
              />
            </div>
            <p className="text-xs text-slate-400">
              La modification sera ajoutée à l'historique du compte rendu, visible en aperçu et sur le PDF.
            </p>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setEditingReport(null)}>Annuler</Button>
              <Button className="flex-1 btn-primary" onClick={handleSaveEdit} disabled={savingEdit} data-testid="save-agent-report-edit-btn">
                {savingEdit ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AgentReportForm;
